import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/auth.service';
import '../styles/SignInPage.css';
import { getErrorMessage } from '../utils/error';

import { PASSWORD_DESCRIPTION, passwordError } from '../utils/passwordPolicy';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    // La règle est celle du backend (`app/domain/password_policy.py`), reprise
    // mot pour mot. Le contrôle local en exigeait SIX : le formulaire acceptait
    // donc un mot de passe que l'API refusait, et l'utilisateur se retrouvait
    // devant un 422 sans explication.
    const probleme = passwordError(password);
    if (probleme) {
      setError(probleme);
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword(token!, password);
      setSuccess(true);
      setTimeout(() => navigate('/signin'), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Une erreur est survenue.'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="signin-container">
        <div className="signin-card">
          <h1 className="signin-title">Coloc App</h1>
          <div className="signin-error">Lien de réinitialisation invalide.</div>
          <div className="signin-links">
            <Link to="/signin" className="forgot-password-link">
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signin-container">
      <div className="signin-card">
        <h1 className="signin-title">Coloc App</h1>
        <h2 className="signin-subtitle">Nouveau mot de passe</h2>

        {success ? (
          <div>
            <p style={{ color: '#27ae60', marginBottom: '1rem' }}>
              Mot de passe réinitialisé avec succès. Redirection vers la page de connexion...
            </p>
          </div>
        ) : (
          <>
            {error && <div className="signin-error">{error}</div>}

            <form onSubmit={handleSubmit} className="signin-form">
              <div className="form-group">
                <label htmlFor="password">Nouveau mot de passe</label>
                <input
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="form-control"
                  placeholder="Entrez votre nouveau mot de passe"
                />
                {/* La règle est annoncée AVANT la saisie : la découvrir par un
                    refus après coup fait retaper deux fois. */}
                <p className="signin-hint">{PASSWORD_DESCRIPTION}</p>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
                <input
                  type="password"
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="form-control"
                  placeholder="Confirmez votre mot de passe"
                />
              </div>

              <button
                type="submit"
                className="signin-button"
                disabled={loading}
              >
                {loading ? 'Réinitialisation...' : 'Réinitialiser'}
              </button>
            </form>

            <div className="signin-links">
              <Link to="/signin" className="forgot-password-link">
                Retour à la connexion
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
